import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Search, FileText, Download, Info, CheckSquare, AlertTriangle } from 'lucide-react';
import { getPODById } from '../services/lambdaService';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 0.4fr 1fr;
  gap: 40px;
  align-items: start;
  margin-top: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const HeroSection = styled(motion.div)`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h1`
  font-family: 'Playfair Display', serif;
  font-size: 48px;
  font-weight: 700;
  color: #333;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #d4af37 0%, #b8941f 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-size: 20px;
  color: #666;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const SearchSection = styled(motion.div)`
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.1);
  padding: 30px;
  margin-bottom: 30px;
  border: 1px solid #971b2f;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 20px;
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px 20px 16px 50px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-size: 16px;
  font-family: 'Inter', sans-serif;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #d4af37;
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  width: 20px;
  height: 20px;
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 20px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  background: #d4af37;
  color: #ffffff;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #d4af37;
    color: #d4af37;
    background: #ffffff;
  }
`;

const RefreshButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 20px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #d4af37 0%, #b8941f 100%);
  color: white;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(212, 175, 55, 0.3);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(212, 175, 55, 0.4);
  }
`;

const ResultsSection = styled(motion.div)`
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.1);
  padding: 30px;
  border: 1px solid #971b2f;
`;

const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;
`;

const ResultsCount = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #333;
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FileItem = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 12px;
  border-left: 4px solid #d4af37;
  transition: all 0.3s ease;

  &:hover {
    background: #f0f0f0;
    transform: translateX(4px);
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
`;

const FileIcon = styled(FileText)`
  width: 32px;
  height: 32px;
  color: #d4af37;
`;

const FileDetails = styled.div`
  flex: 1;
`;

const FileName = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
  font-size: 16px;
`;

const FileMeta = styled.div`
  display: flex;
  gap: 20px;
  font-size: 14px;
  color: #666;
`;

const FileSize = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FileDate = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FileStatus = styled.div`
  font-size: 14px;
  color: #666;
`;

const EmptyState = styled(motion.div)`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const EmptyIcon = styled(FileText)`
  width: 64px;
  height: 64px;
  color: #ccc;
  margin-bottom: 20px;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const LoadingSpinner = styled(FileText)`
  width: 32px;
  height: 32px;
  color: #d4af37;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
`;

const DownloadButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(40, 167, 69, 0.4);
  }

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const InfoCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.1);
  padding: 30px;
  border: 1px solid #971b2f;
  height: fit-content;
  position: sticky;
  top: 20px;
`;

const InfoTitle = styled.h2`
  font-family: 'Playfair Display', serif;
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin-bottom: 24px;
  background: linear-gradient(135deg, #d4af37 0%, #b8941f 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const InfoSection = styled.div`
  margin-bottom: 20px;
`;

const InfoSectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const InfoListItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
  font-size: 13px;
  color: #666;
  line-height: 1.4;
`;

const InfoIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, #d4af37 0%, #b8941f 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
`;

const InfoIconInner = styled(CheckSquare)`
  width: 12px;
  height: 12px;
  color: white;
`;

const FeatureHighlight = styled.div`
  background: linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(184, 148, 31, 0.1) 100%);
  border-radius: 12px;
  padding: 16px;
  margin-top: 20px;
  border-left: 4px solid #d4af37;
`;

const FeatureTitle = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FeatureDescription = styled.div`
  font-size: 14px;
  color: #666;
  line-height: 1.5;
`;

const DownloadPODs = () => {
  const [searchId, setSearchId] = useState('');
  const [pod, setPod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const searchPOD = async () => {
    if (!searchId.trim()) {
      setError('Please enter a POD ID');
      return;
    }

    setLoading(true);
    setError('');
    setPod(null);

    try {
      const podData = await getPODById(searchId.trim());
      if (podData.error) {
        setError(podData.error);
      } else {
        setPod(podData);
      }
    } catch (error) {
      console.error('Failed to get POD:', error);
      setError('POD not found or error occurred');
    } finally {
      setLoading(false);
    }
  };

  const downloadPOD = async () => {
    if (!pod || !pod.fileData) {
      setError('No file data available for download');
      return;
    }

    setDownloading(true);
    setError('');

    try {
      const byteCharacters = atob(pod.fileData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: pod.contentType || 'application/octet-stream' });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = pod.fileName || `pod-${pod.id}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      setError('Failed to download file');
    } finally {
      setDownloading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Container>
      <HeroSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <Title>Search POD Documents</Title>
        <Subtitle>
          Search for specific POD document by ID.
        </Subtitle>
      </HeroSection>

      <SearchSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <SearchContainer>
          <SearchInput>
            <SearchIcon />
            <Input
              type="text"
              placeholder="Enter POD ID to search..."
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchPOD()}
            />
          </SearchInput>
          <FilterButton onClick={searchPOD} disabled={loading}>
            <Search size={20} />
            {loading ? 'Searching...' : 'Search'}
          </FilterButton>
        </SearchContainer>
      </SearchSection>

      <ContentWrapper>
        <InfoCard
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <InfoTitle>
            <Info size={24} />
            How to Use This Service
          </InfoTitle>
          <InfoSection>
            <InfoSectionTitle>
              <Search size={16} />
              Search Process
            </InfoSectionTitle>
            <InfoList>
              <InfoListItem>
                <InfoIcon>
                  <InfoIconInner />
                </InfoIcon>
                Enter the POD ID in the search field above.
              </InfoListItem>
              <InfoListItem>
                <InfoIcon>
                  <InfoIconInner />
                </InfoIcon>
                Click "Search" to find your POD document.
              </InfoListItem>
              <InfoListItem>
                <InfoIcon>
                  <InfoIconInner />
                </InfoIcon>
                Once found, click "Download" to retrieve your file.
              </InfoListItem>
            </InfoList>
          </InfoSection>

          <InfoSection>
            <InfoSectionTitle>
              <AlertTriangle size={16} />
              Warning
            </InfoSectionTitle>
            <InfoList>
              <InfoListItem>
                <InfoIcon>
                  <InfoIconInner />
                </InfoIcon>
                PODS are loaded next day of delivery
              </InfoListItem>
              <InfoListItem>
                <InfoIcon>
                  <InfoIconInner />
                </InfoIcon>
                For Armellini, Florida Beauty, please contact International Logistics Team.
              </InfoListItem>
            </InfoList>
          </InfoSection>
        </InfoCard>

        <ResultsSection
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <ResultsHeader>
            <ResultsCount>
              {loading ? 'Searching...' : pod ? 'POD Found' : 'Search for a POD'}
            </ResultsCount>
          </ResultsHeader>

          {loading ? (
            <LoadingState>
              <LoadingSpinner />
              <div>Searching for POD...</div>
            </LoadingState>
          ) : error ? (
            <EmptyState
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
            >
              <EmptyIcon />
              <div style={{ fontSize: '18px', marginBottom: '8px', color: '#dc3545' }}>
                {error}
              </div>
              <div style={{ fontSize: '14px', color: '#999' }}>
                Please check the POD ID and try again
              </div>
            </EmptyState>
          ) : pod ? (
            <FileList>
              <FileItem
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <FileInfo>
                  <FileIcon />
                  <FileDetails>
                    <FileName>{pod.fileName}</FileName>
                    <FileMeta>
                      <FileSize>
                        {formatFileSize(pod.size)}
                      </FileSize>
                    </FileMeta>
                  </FileDetails>
                </FileInfo>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FileStatus>
                    <span style={{ color: '#28a745', fontWeight: '500' }}>
                      Available
                    </span>
                  </FileStatus>
                  <DownloadButton
                    onClick={downloadPOD}
                    disabled={downloading || !pod.fileData}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Download size={16} />
                    {downloading ? 'Downloading...' : 'Download'}
                  </DownloadButton>
                </div>
              </FileItem>
            </FileList>
          ) : (
            <EmptyState
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
            >
              <EmptyIcon />
              <div style={{ fontSize: '18px', marginBottom: '8px' }}>
                Search for a POD Document
              </div>
              <div style={{ fontSize: '14px', color: '#999' }}>
                Enter a POD ID above to search for a specific document
              </div>
            </EmptyState>
          )}
        </ResultsSection>
      </ContentWrapper>
    </Container >
  );
};

export default DownloadPODs;